<template>
  <div
    v-if="videoShowcaseEvents.length > 0"
    ref="showcaseRef"
    class="video-event-showcase min-h-[200px] mb-10 mt-10"
    data-test-id="magazine-article-video-event-showcase"
  >
    <Heading
      v-if="showcaseTitle"
      :title="showcaseTitle"
      :level="2"
      h-class="text-h3 font-semibold"
      :use-h-tag="false"
      class="mb-3 md:mb-2 kft-product-showcase__heading kft-heading--left"
    />

    <div class="video-event-showcase__grid">
      <div
        v-for="(event, index) in videoShowcaseEvents"
        :key="eventGetters.getPermalink(event)"
        class="video-event-showcase__item"
      >
        <MagazineVideoEventShowcaseCard
          :event="event"
          :card-index="index"
          @click:video-event="trackProductCardClick"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import * as qs from 'qs'
import type { ComputedRef } from 'vue'
import type { EventDescription } from '~/composables/types/event'
import type { StrapiMagazineArticle } from '~/composables/types/strapi/strapi-magazine'
import { useMagazineVideoEventShowcasePlaybackProvider } from '~/composables/magazine/useMagazineVideoEventShowcasePlayback'
import { mergeKftEventDescriptionSettingsIncludes } from '~/utils/kftEventDescriptionIncludes'
import { GtmItemListId } from '~/composables/useKftGtm/types'
import {
  sendSelectListItem,
  sendViewItemList,
} from '~/composables/useGtmEvents'

const route = useRoute()

const article = inject('article') as ComputedRef<StrapiMagazineArticle | null>

const eventPermalinks = computed(
  () =>
    article.value?.events_video_showcase?.events_permalinks
      ?.map((item) => item.event_permalink)
      .filter(Boolean) ?? []
)

const showcaseTitle = computed(
  () => article.value?.events_video_showcase?.title ?? ''
)

const hasPlayablePortraitVideo = (event: EventDescription) => {
  const video = eventGetters.getPortraitVideo(event)
  return Boolean(video?.original || video?.streaming_url)
}

const fetchEventsByPermalinks = async (
  permalinks: string[]
): Promise<EventDescription[]> => {
  if (!permalinks.length) return []

  const responses = await Promise.all(
    permalinks.map(async (permalink) => {
      try {
        const response = await kftApiRequest<EventDescription>(
          `v1/store/events/${permalink}?${qs.stringify(
            {
              include: mergeKftEventDescriptionSettingsIncludes([
                'supplier',
                'reviews',
              ]),
            },
            { arrayFormat: 'comma' }
          )}`,
          { method: 'GET' }
        )

        return response.data ?? null
      } catch (error) {
        console.error(`Failed to fetch event ${permalink}:`, error)
        return null
      }
    })
  )

  const eventsByPermalink = new Map(
    responses
      .filter((event): event is EventDescription => Boolean(event))
      .map((event) => [eventGetters.getPermalink(event), event])
  )

  return permalinks
    .map((permalink) => eventsByPermalink.get(permalink))
    .filter((event): event is EventDescription => {
      if (!event) return false
      return hasPlayablePortraitVideo(event)
    })
}

const { data: fetchedEvents } = await useAsyncData(
  () =>
    `magazine-video-event-showcase-${route.params.articleSlug}-${eventPermalinks.value.join(',')}`,
  () => fetchEventsByPermalinks(eventPermalinks.value),
  { watch: [eventPermalinks] }
)

const videoShowcaseEvents = computed(() => fetchedEvents.value ?? [])

const videoCardIndices = computed(() =>
  videoShowcaseEvents.value.map((_, index) => index)
)

const trackProductCardClick = (index: number) => {
  const event = videoShowcaseEvents.value[index]
  if (!event) return

  sendSelectListItem(
    GtmItemListId.MagazineArticlePageVideoClasses,
    [event],
    index
  )
}

const hasTrackedGtmView = ref(false)

const trySendViewItemList = () => {
  if (hasTrackedGtmView.value || !videoShowcaseEvents.value.length) return

  sendViewItemList(
    GtmItemListId.MagazineArticlePageVideoClasses,
    videoShowcaseEvents.value
  )
  hasTrackedGtmView.value = true
}

const { setShowcaseVisible } =
  useMagazineVideoEventShowcasePlaybackProvider(videoCardIndices)

const showcaseRef = ref<HTMLElement | null>(null)

onMounted(() => {
  if (!showcaseRef.value) return

  const observer = new IntersectionObserver(
    ([entry]) => {
      const isIntersecting = entry?.isIntersecting ?? false
      setShowcaseVisible(isIntersecting)

      if (isIntersecting) {
        trySendViewItemList()
      }
    },
    { rootMargin: '0px 0px 10% 0px', threshold: 0.1 }
  )

  observer.observe(showcaseRef.value)

  onUnmounted(() => {
    observer.disconnect()
    setShowcaseVisible(false)
  })
})

watch(videoShowcaseEvents, () => {
  trySendViewItemList()
})
</script>

<style lang="css" scoped>
@reference '~/assets/css/tailwind.css';
.video-event-showcase__grid {
  @apply flex flex-nowrap gap-2 overflow-x-auto snap-x snap-mandatory pt-1 -mx-4;
  @apply md:grid md:grid-cols-6 md:gap-4 md:overflow-visible md:snap-none md:mx-0 md:px-0;
  scrollbar-width: none;
  scroll-padding-left: 1rem;
  scroll-padding-right: 1rem;
}

.video-event-showcase__grid::before,
.video-event-showcase__grid::after {
  content: '';
  flex: 0 0 1rem;
}

@media (min-width: 768px) {
  .video-event-showcase__grid {
    scroll-padding-left: 0;
    scroll-padding-right: 0;
  }

  .video-event-showcase__grid::before,
  .video-event-showcase__grid::after {
    display: none;
  }
}

.video-event-showcase__grid::-webkit-scrollbar {
  display: none;
}

.video-event-showcase__item {
  @apply shrink-0 snap-start;
  width: min(124px, 55vw);

  @media (min-width: 768px) {
    width: auto;
  }
}
</style>
